<template>
  <div class="Form">
    <div
      v-for="(form, index) in data"
      :key="index"
      v-html="decodeFormData(form.base64EncodedHtml)"
    />
  </div>
</template>

<script>
export default {
  name: "FormComponent",
  props: {
    data: {
      type: Array,
      default () {
        return []
      }
    }
  },
  mounted () {
    let checkboxes = document.querySelectorAll("input[type=checkbox]")
    let radios = document.querySelectorAll("input[type=radio]")

    this.traverseDOMRadiosCheckboxes(
      checkboxes,
      "checkbox__input",
      "checkbox__label",
      "checkbox"
    )
    this.traverseDOMRadiosCheckboxes(
      radios,
      "radio__input",
      "radio__label",
      "radio"
    )
  },
  methods: {
    decodeFormData (str) {
      return !SSR_MODE
        ? this.b64DecodeUnicode(str)
        : Buffer.from(str, "base64").toString("utf8")
    },
    b64DecodeUnicode (str) {
      // Going backwards: from bytestream, to percent-encoding, to original string.
      return decodeURIComponent(
        atob(str)
          .split("")
          .map(function (c) {
            return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2)
          })
          .join("")
      )
    },
    traverseDOMRadiosCheckboxes (
      elements,
      elementClassName,
      labelClassName,
      parentClassName
    ) {
      if (elements.length) {
        elements.forEach(element => {
          let uniqueId = Math.random()
          let label = element.parentNode
          element.classList.add(elementClassName)
          element.setAttribute("id", uniqueId)
          label.classList.add(labelClassName)
          label.setAttribute("for", uniqueId)
          let wrapper = document.createElement("div")
          wrapper.classList.add(parentClassName)
          label.parentNode.insertBefore(wrapper, label)
          wrapper.appendChild(label)
          wrapper.insertBefore(element, wrapper.childNodes[0])
        })
      }
    }
  }
}
</script>

<style lang="scss">
.Form {
  $this: &;

  .FormTextbox {
    &__Input {
      @include font(text, 3, "normal");

      color: $color-grey-dark;
      width: 100%;
      background: $color-grey-lightest;
      border: 0.0625rem solid $color-grey-light;
      border-radius: 0.25rem;
      padding: 0.75rem 1rem 0.75rem 1rem;
      transition: padding-top 0.1s ease-in-out;
      height: 48px;
    }

    &--Textarea {
      .FormTextbox__Input {
        height: 144px;
      }
    }
  }

  & .FormSelection:not(.ValidationRequired),
  & .FormTextbox:not(.ValidationRequired) {
    .Form__Element__Caption::after {
      content: " (Valfritt)";
    }
  }

  &__Title {
    @include font(rubrik, 2, "bold");
  }

  &__Description {
    @include font(text, 3, "normal");
  }

  &__Element {
    $this: &;

    margin-bottom: 24px;

    &.FormSubmitButton {
      font-family: "ICA Text", sans-serif;
      font-weight: bold;
      font-size: 1rem;
      padding: 1rem 1.5rem;
      line-height: 1;
      border-radius: 3.125rem;
      appearance: none;
      border: 0;
      background-color: $color-primary-cta;
      color: $color-white;
      fill: #fff;
      cursor: pointer;
      text-decoration: none;
      display: inline-block;
      text-align: center;
      width: 100%;

      &:hover {
        background-color: $color-pink-light;
        color: $color-white;
        fill: $color-white;
      }

      &:disabled {
        background-color: $color-grey-special;
        color: $color-inactive-text;
        fill: $color-inactive-text;
        pointer-events: none;
      }

      &:active {
        background-color: $color-red-dark;
        color: $color-white;
        fill: $color-white;
      }
    }

    &.FormChoice {
      .checkbox,
      .radio {
        display: inline-flex;
      }
    }
  }

  #{$this}.ValidationFail .FormTextbox__Input {
    background-color: $color-orange-lightest;
    border-color: $color-red-special;
  }

  #{$this}__Caption {
    @include font(text, 3, "bold");

    display: block;
    margin-bottom: 8px;
  }

  #{$this}__ValidationError {
    @include font(text, 1, "normal");

    color: $color-red;
    margin-top: 0.5rem;
    display: block;
  }

  select {
    display: block;
    color: $color-grey-dark;
    width: 100%;
    background: $color-grey-lightest;
    border: 0.0625rem solid $color-grey-light;
    border-radius: 0.25rem;
    padding: 0.75rem 1rem;
    background-image: url("data:image/svg+xml, %3Csvg class='custom-select__angle-icon' xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 32 32' %3E%3Cpath fill-rule='evenodd' d='M27.9111009,10.5285118 L26.7651009,9.55951178 C26.6601009,9.46951178 26.5031009,9.48351178 26.4141009,9.58851178 L16.0001009,21.9525118 L5.58610087,9.58851178 C5.49710087,9.48351178 5.34010087,9.46951178 5.23410087,9.55951178 L4.08810087,10.5285118 C3.98310087,10.6175118 3.97010087,10.7755118 4.05910087,10.8805118 L15.2351009,24.1445118 C15.6351009,24.6175118 16.3651009,24.6175118 16.7651009,24.1445118 L27.9401009,10.8805118 C28.0301009,10.7755118 28.0161009,10.6175118 27.9111009,10.5285118' /%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 1rem top 50%, 0 0;
    background-size: 1rem auto, 100%;
    appearance: none;

    @include font(text, 3, "normal");

    &::-ms-expand {
      display: none;
    }
  }

  #{$this}.ValidationFail {
    select {
      background-color: $color-orange-lightest;
      border-color: $color-red-special;
    }
  }

  fieldset {
    border: 0;
    padding: 0;
  }
}

.EPiServerForms {
  .Form__NavigationBar {
    .Form__NavigationBar__ProgressBar {
      display: none !important; // this styling is set to override styling from EPI and hide pagination progress bar as per design
    }

    .Form__NavigationBar__Action {
      cursor: pointer;
      text-decoration: none;
      display: inline-block;
      text-align: center;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;

      @include font(text, 3, "bold");

      background-color: transparent;
      color: $color-secondary-cta-text;
      fill: $color-secondary-cta-text;

      &.btnNext,
      &.btnPrev {
        border: 0;
        max-width: 100%;
        background-image: none;
        width: auto;
        height: auto;
      }

      &.btnNext {
        float: right;
        background-color: $color-primary-cta;
        color: $color-white;
        fill: $color-white;
        padding: 0.75rem 1.5rem;
        border-radius: 3.125rem;

        &:hover {
          background-color: $color-pink-light;
          color: $color-white;
          fill: $color-white;
        }

        &:disabled {
          background-color: $color-grey-special;
          color: $color-inactive-text;
          fill: $color-inactive-text;
          pointer-events: none;
        }

        &:active {
          background-color: $color-red-dark;
          color: $color-white;
          fill: $color-white;
        }

        @include media-breakpoint-up(md) {
          min-width: 10.9375rem;
        }
      }

      &.btnPrev {
        float: left;
        position: relative;
        padding-left: 2rem;
        color: $color-secondary-cta-text;
        fill: $color-secondary-cta-text;

        &:disabled {
          display: none;
        }

        &::before {
          content: " ";
          background-size: contain;
          background-image: url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiB2aWV3Qm94PSIwIDAgMzIgMzIiPjxkZWZzPjxwYXRoIGlkPSJhIiBkPSJNMjYuNzUgMTUuMDAxSDguNzQybDcuMTctNmEuMjUuMjUgMCAwIDAgLjAyOS0uMzUybC0uOTY5LTEuMTQ2YS4yNS4yNSAwIDAgMC0uMzUyLS4wMjlsLTkuMjY2IDcuNzYyYy0uNDcyLjQtLjQ3MiAxLjEzIDAgMS41M2w5LjI2NiA3Ljc2MmEuMjUuMjUgMCAwIDAgLjM1Mi0uMDI5bC45NjktMS4xNDVhLjI1MS4yNTEgMCAwIDAtLjAyOS0uMzUzbC03LjE3LTZIMjYuNzVhLjI1LjI1IDAgMCAwIC4yNS0uMjV2LTEuNWEuMjUuMjUgMCAwIDAtLjI1LS4yNSIvPjwvZGVmcz48dXNlIGZpbGw9IiNjZjJlMDUiIGZpbGwtcnVsZT0iZXZlbm9kZCIgeGxpbms6aHJlZj0iI2EiLz48L3N2Zz4=");
          left: 1rem;
          top: 50%;
          transform: translate(-50%, -50%);
          z-index: 2;
          width: 1.5rem;
          height: 1.5rem;
          display: block;
          position: absolute;
        }
      }
    }
  }

  .FormCaptcha {
    display: flex;
    flex-direction: column;

    &__Refresh {
      order: 1;
      margin: 0 auto;
      padding: 0.625rem 1.5rem 0.626rem 3rem;
      background-color: #fcece7;
      color: $color-secondary-cta-text;
      fill: $color-secondary-cta-text;
      text-decoration: none;
      display: inline-block;
      text-align: center;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 100%;
      cursor: pointer;
      border: 0;
      border-radius: 3.125rem;
      position: relative;

      @include font(text, 3, "bold");

      &::before {
        content: " ";
        background-size: contain;
        background-image: url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiB2aWV3Qm94PSIwIDAgMzIgMzIiPjxkZWZzPjxwYXRoIGlkPSJhIiBkPSJNMjEgMTEuMjV2MS41YzAgLjEzOC4xMTIuMjUuMjUuMjVoNS41YS4yNS4yNSAwIDAgMCAuMjUtLjI1di01LjVhLjI1LjI1IDAgMCAwLS4yNS0uMjVoLTEuNWEuMjUuMjUgMCAwIDAtLjI1LjI1djIuNDU1Yy0yLjI2LTMuMjI2LTYuMTkzLTUuMTkyLTEwLjUzNy00LjYtNC45LjY2OC04LjgyMyA0LjY4Ni05LjM4OCA5LjU5OUM0LjMxIDIxLjM0NyA5LjUwOCAyNyAxNiAyN2M1Ljk3OSAwIDEwLjg1OC00Ljc5NCAxMC45OTctMTAuNzRhLjI1Ny4yNTcgMCAwIDAtLjI1Ni0uMjZIMjUuMjRhLjI0My4yNDMgMCAwIDAtLjI0My4yNDJDMjQuODY3IDIxLjA5MiAyMC44ODIgMjUgMTYgMjVjLTUuMDU4IDAtOS4xNTQtNC4xOTItOC45OTYtOS4yODUuMTQ5LTQuNzk0IDQuMjI3LTguNzMgOS4wMjItOC43MTUgMy4xLjAwOSA1LjgyNyAxLjYgNy40MzcgNEgyMS4yNWEuMjUuMjUgMCAwIDAtLjI1LjI1Ii8+PC9kZWZzPjx1c2UgZmlsbD0iI2NmMmUwNSIgZmlsbC1ydWxlPSJldmVub2RkIiB4bGluazpocmVmPSIjYSIvPjwvc3ZnPg==");
        left: 2rem;
        top: 50%;
        transform: translate(-50%, -50%);
        z-index: 2;
        width: 1.5rem;
        height: 1.5rem;
        display: block;
        position: absolute;
      }
    }

    &__Image {
      order: 0;
      margin: 0 auto;
      margin-bottom: 1rem;
    }

    &__Input.FormHideInSummarized {
      order: 2;
      margin: 1rem auto 0 auto;
    }

    .Form__Element__ValidationError {
      text-align: center;
      margin-bottom: 0.625rem;
    }
  }

  .FormResetButton {
    cursor: pointer;
    text-decoration: none;
    display: inline-block;
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    border: 0;
    border-radius: 3.125rem;

    @include font(text, 3, "bold");

    background-image: none;
    background-color: transparent;
    color: $color-secondary-cta-text;
    fill: $color-secondary-cta-text;
    width: auto;
    height: auto;

    &:hover {
      color: $color-pink-light;
    }
  }
}

.FormFileUpload__Input {
  color: $color-grey-dark;
  width: 100%;
  background: $color-grey-lightest;
  border: 0.0625rem solid $color-grey-light;
  border-radius: 0.25rem;
  padding: 0.75rem 1rem 0.75rem 1rem;
  transition: padding-top 0.1s ease-in-out;
  height: 48px;
  position: relative;

  &::after {
    content: " ";
    background-size: contain;
    background-image: url("data:image/svg+xml;base64,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");
    right: 0;
    top: 50%;
    transform: translate(-50%, -50%);
    z-index: 2;
    width: 1.5rem;
    height: 1.5rem;
    display: block;
    position: absolute;
  }
}
</style>
